import type { DesignItem } from './types';

export interface SmartLayoutMeta {
  placeholder?: boolean;
  placeholderRole?: 'text' | 'image' | 'logo';
  locked?: boolean;
  autoFit?: 'contain' | 'cover' | 'text-shrink';
  align?: { h: 'left' | 'center' | 'right'; v: 'top' | 'center' | 'bottom' };
  required?: boolean;
}

export const SMART_LAYOUT_VERSION = 1;

function asRecord(item: DesignItem): Record<string, unknown> {
  return item as Record<string, unknown>;
}

export function readSmartLayout(item: DesignItem): SmartLayoutMeta {
  const ex = asRecord(item);
  const raw = ex.smartLayout;
  if (!raw || typeof raw !== 'object') return {};
  return { ...(raw as SmartLayoutMeta) };
}

export function isItemLocked(item: DesignItem): boolean {
  const ex = asRecord(item);
  if (ex.locked === true) return true;
  return readSmartLayout(item).locked === true;
}

export function isPlaceholder(item: DesignItem): boolean {
  return readSmartLayout(item).placeholder === true;
}

export function placeholderLabel(item: DesignItem): string {
  const role = readSmartLayout(item).placeholderRole;
  if (role === 'logo') return 'Logo';
  if (role === 'image') return 'Image';
  if (role === 'text') return 'Text';
  return 'Placeholder';
}

/** Apply smart-layout flags when loading a template into the designer. */
export function normalizeTemplateItem(item: DesignItem): DesignItem {
  const next = { ...item } as Record<string, unknown>;
  const layout = readSmartLayout(item);
  if (layout.locked) {
    next.locked = true;
  }
  if (Object.keys(layout).length > 0) {
    next.smartLayout = { ...layout };
  }
  return next as DesignItem;
}

export interface PrintableBounds {
  width: number;
  height: number;
}

function alignOffset(
  align: SmartLayoutMeta['align'],
  slotW: number,
  slotH: number,
  contentW: number,
  contentH: number,
): { left: number; top: number } {
  const h = align?.h ?? 'center';
  const v = align?.v ?? 'center';
  let left = 0;
  let top = 0;
  if (h === 'left') left = 0;
  else if (h === 'right') left = slotW - contentW;
  else left = (slotW - contentW) / 2;
  if (v === 'top') top = 0;
  else if (v === 'bottom') top = slotH - contentH;
  else top = (slotH - contentH) / 2;
  return { left, top };
}

/** Auto-fit placeholder slot content within its box when user adds text/image. */
export function applyPlaceholderAutoFit(
  item: DesignItem,
  patch: Partial<DesignItem>,
  bounds?: PrintableBounds,
): Partial<DesignItem> | null {
  if (!isPlaceholder(item)) return null;
  const layout = readSmartLayout(item);
  const autoFit = layout.autoFit ?? (item.type === 'text' ? 'text-shrink' : 'contain');
  const slotW = Math.max(1, Number(item.width) || 1);
  const slotH = Math.max(1, Number(item.height) || 1);
  const hasText = Object.prototype.hasOwnProperty.call(patch, 'text');
  const hasSrc = Object.prototype.hasOwnProperty.call(patch, 'src');
  if (!hasText && !hasSrc) return null;

  const next: Partial<DesignItem> = { ...patch };

  if (item.type === 'text' && hasText && autoFit === 'text-shrink') {
    const textLen = String(patch.text ?? item.text ?? '').length || 1;
    const baseSize = Number(item.fontSize ?? 24);
    const shrink = Math.min(1, Math.max(0.35, slotW / Math.max(40, textLen * baseSize * 0.55)));
    next.fontSize = Math.max(8, Math.round(baseSize * shrink));
  }

  if ((item.type === 'image' || item.type === 'svg') && hasSrc) {
    const sourceW = Number((patch as Record<string, unknown>).sourcePixelWidth ?? item.sourcePixelWidth ?? slotW);
    const sourceH = Number((patch as Record<string, unknown>).sourcePixelHeight ?? item.sourcePixelHeight ?? slotH);
    const ratio = sourceW / Math.max(1, sourceH);
    let fitW = slotW;
    let fitH = slotH;
    if (autoFit === 'contain') {
      if (slotW / slotH > ratio) {
        fitW = slotH * ratio;
      } else {
        fitH = slotW / ratio;
      }
    } else if (autoFit === 'cover') {
      if (slotW / slotH > ratio) {
        fitH = slotW / ratio;
      } else {
        fitW = slotH * ratio;
      }
    }
    const offset = alignOffset(layout.align, slotW, slotH, fitW, fitH);
    next.width = fitW;
    next.height = fitH;
    next.left = Number(item.left ?? 0) + offset.left;
    next.top = Number(item.top ?? 0) + offset.top;
  }

  if (layout.align && bounds) {
    // Keep slot anchored within printable area when content changes.
    void bounds;
  }

  next.smartLayout = { ...layout, placeholder: false };
  return next;
}
